import {Component, h, Listen, Method, Prop, State, Event, EventEmitter} from "@stencil/core";
import {createSession, fetchGoToLocalInfo} from "../../services/api";
import {GlobalIdValidation, IGoToLocalInfo} from "../../models/go-to-local-info";
import {mergeUrlWithQueryParameters} from "../../utils/utils";
import {getCountryFromUrl, getLanguageFromUrl} from "../../services/i18n";


interface IDisplayParameters {
    noInfo: boolean
    parameters?: {
        selectedLocation?: string
        selectedRegion?: string
        showRedirectInfo: boolean
        showInvalidContentInfo: boolean
    }
}


@Component({
    tag: 'dc-go-to-local-modal',
    styleUrl: '../../assets/styles/style.scss',
    shadow: true,
})
export class DcLocationSelectorModal {
    @Prop({mutable: true}) dialogTitle = 'More about our products';
    @Prop({mutable: true}) buttonLablePrefix = "To the site of ";
    @Prop({mutable: true}) buttonLabelNoContentPrefix = "To the local contact of ";
    @Prop({mutable: true}) pageId = '';
    pageInfo?: IGoToLocalInfo;

    @Prop({mutable: true}) shouldShow = false;
    @Prop({mutable: true}) suggestedLocation?: string;
    @Prop({mutable: true}) suggestedRegion?: string;
    @Prop({mutable: true}) validGlobalPageId?: GlobalIdValidation;
    @Prop() serverUrl?: string;
    @Prop() sessionUrl?: string;
    @Prop({mutable: true}) closable = true;


    @State() delayedShow = false;
    private triggers: { element: Element, binding: EventListenerOrEventListenerObject }[] = [];
    protected linkType: string = 'EXTERNAL';
    @State() isInputDisabled = false;
    @Event() goToLocalModalShareInformation : EventEmitter<{ countryOfDestination: string, languageOfDestination: string, keepSelection?: boolean, triggerElement?: HTMLElement}>;
    protected isChecked = false;
    protected triggerElement?: HTMLElement;


    constructor() {
        const observer = new MutationObserver(() => {
            this.registerTriggerElements();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });


        this.registerTriggerElements();
    }

    private emitLanguageChangeEvent(destinationUrl: URL, keepSelection?: boolean) {
        let country = getCountryFromUrl(destinationUrl);
        let language = getLanguageFromUrl(destinationUrl);

        // Store links are built differently
        if (!country || !language || country.length > 2 || language.length > 2) {
            let locale = destinationUrl.searchParams.get('locale');

            if (!locale) {
                locale = destinationUrl.searchParams.get('cclcl');
            }

            if (locale) {
                const localeTokens = locale.toLowerCase().split('_');
                country = localeTokens[1];
                language = localeTokens[0];
            }
        }

        this.goToLocalModalShareInformation.emit({
            countryOfDestination: country,
            languageOfDestination: language,
            keepSelection: keepSelection ?? this.isChecked,
            triggerElement: this.triggerElement,
        });
    }

    @Method()
    public async hideDialog() {
        this.shouldShow = false;
        this.delayedShow = false;
        this.triggerElement = undefined;
        this.setModalStatus(false);
    }

    @Method()
    public async registerTriggerElements() {
        const registerAll = () => {
            document
                .querySelectorAll('*[data-gtl-global-page-id]')
                .forEach(element => {
                    const binding = this.showDialog.bind(this, element);
                    this.triggers.push({element, binding});
                    element.addEventListener('click', binding);
                });
        };
        const unregisterAll = () => {
            this.triggers.forEach(trigger => trigger.element.removeEventListener('click', trigger.binding));
        };

        unregisterAll();
        registerAll();
    }

    @Method()
    public async showDialog(triggerElement: HTMLElement, event: globalThis.Event) {
        event.preventDefault();

        this.triggerElement = triggerElement;
        this.pageId = triggerElement.getAttribute('data-gtl-global-page-id');
        this.dialogTitle = triggerElement.getAttribute('data-gtl-dialog-title');

        await this.showDialogOnLoad(
            this.pageId,
            this.linkType,
            this.dialogTitle,
            undefined,
            undefined
        );
    }

    @Method()
    public async showDialogOnLoad(pageId: string, linkType: string, dialogTitle: string, preselection?: string, locale?: string) {
        try {
            this.pageInfo = await fetchGoToLocalInfo({
                pageRefId: pageId,
                serverUrl: this.serverUrl ? new URL(this.serverUrl) : undefined,
                linkType,
                preselection,
                locale
            });
        } finally {
            this.suggestedLocation = this.pageInfo?.target_location;
            this.suggestedRegion = this.pageInfo?.target_region;
            this.validGlobalPageId = this.pageInfo?.global_id_validations?.[pageId] ?? GlobalIdValidation.Valid;
            this.pageId = pageId;
            this.linkType = linkType;
            this.dialogTitle = dialogTitle;
            this.shouldShow = true;
            this.setModalStatus(true);
        }
    }

    @Method()
    public async changeCheckboxValueChecked(isChecked: boolean) {
        const languageCode = this.getLanguageCode();
        const countryCode = this.getCountryCode();

        try {
            await createSession(this.sessionUrl, languageCode, countryCode, isChecked);
        } catch (error) {
            throw Error(error);
        }
        this.isChecked = isChecked;

    }

    private fetchNewLocationInfo() {
        // deactivate input
        this.isInputDisabled = true;

        setTimeout(async () => {
            // fetch info
            const locale = this.pageInfo!.references[this.pageId][this.suggestedRegion][this.suggestedLocation].locale;
            await this.showDialogOnLoad(
                this.pageId,
                this.linkType,
                this.dialogTitle,
                `${this.suggestedLocation}_${this.suggestedRegion}`,
                locale,
            );

            // activate input
            this.isInputDisabled = false;
        });
    }

    render() {

        if (!this.shouldShow) {
            return;
        }

        const {noInfo, parameters} = this.getDisplayParameters();
        let locations = [];
        let regions = [];

        if (!noInfo) {
            if (parameters.selectedRegion) {
                locations = Object.keys(this.pageInfo?.references[this.pageId][parameters.selectedRegion]).sort() ?? [];
            }
            regions = Object.keys(this.pageInfo?.references[this.pageId]).sort() ?? [];
        }

        setTimeout(() => {
            this.delayedShow = true;
        });

        if (this.pageInfo.gtlSessionRedirectURL) {
            this.redirectToTargetURL(this.pageInfo.gtlSessionRedirectURL);
        }

        return (
            <div
                class={`module-modal modal modal-dark show ${this.delayedShow && 'animate'}`}
                data-plugin="moduleModal"
                onClick={(event: UIEvent) => this.checkForClose(event)}
            >
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-close">
                            {this.closable &&
                                <div class="module-button">
                                    <button
                                        aria-label="close"
                                        class="btn btn-link"
                                        onClick={() => this.hideDialog()}
                                        type="button"
                                    ></button>
                                    <div class="button-animation"></div>
                                    <div class="button-content">
                                        <i class="icon icon-dc_close"></i>
                                    </div>
                                </div>
                            }
                        </div>

                        {!noInfo &&
                            <div class="modal-header">
                                <p class="headline h5 modal-title">
                                    {this.dialogTitle}
                                </p>
                            </div>
                        }

                        {!noInfo &&
                            <div class="modal-body">
                                <dc-region-selector
                                    regionLabel={'Please choose region'}
                                    regionPlaceholder={'-- SELECT YOUR REGION --'}
                                    regions={regions.map(region => ({
                                        id: region,
                                        label: region,
                                        selected: region === parameters.selectedRegion,
                                    }))}

                                    disableInputs={this.isInputDisabled}

                                    locationLabel={'Please choose location'}
                                    locationPlaceholder={'-- SELECT YOUR LOCATION --'}
                                    locations={locations.map(location => ({
                                        id: location,
                                        label: location,
                                        selected: location === this.suggestedLocation,
                                    }))}
                                ></dc-region-selector>

                                {parameters.showRedirectInfo &&
                                    <div class="module-callout callout-information has-icon"
                                         data-plugin="moduleCallout"
                                         data-display-time="">
                                        <i class="icon"></i>
                                        <div class="text-wrapper">
                                            <p class="headline">
                                                There is no Rexroth website for {this.suggestedLocation}
                                            </p>
                                            <p class="paragraph">
                                                You will be supported by Rexroth in {parameters.selectedLocation} {this.getLanguageLabel()}.
                                            </p>
                                        </div>
                                    </div>
                                }

                                {parameters.showInvalidContentInfo &&
                                    <div class="module-callout callout-information has-icon"
                                         data-plugin="moduleCallout"
                                         data-display-time="">
                                        <i class="icon"></i>
                                        <div class="text-wrapper">
                                            <p class="headline">
                                                Sorry we did not find the content you are looking for
                                            </p>
                                            <p class="paragraph">
                                                You will be forwarded to the local contact page {parameters.showRedirectInfo ? `of Rexroth in ${parameters.selectedLocation}` : ''}
                                            </p>
                                        </div>
                                    </div>
                                }

                                {parameters.selectedRegion && parameters.selectedLocation &&
                                    <div class="module-button-group" style={{ marginBottom: '1.5rem' }}>
                                        <div class="module-button modal-continue" style={{margin: 'auto', overflow: 'visible'}}>
                                            <button
                                                aria-label={this.getButtonText(parameters.selectedLocation)}
                                                class="btn btn-primary"
                                                onClick={() => this.openLocation(parameters.selectedRegion, this.suggestedLocation) }

                                                type="button"
                                                disabled={this.isInputDisabled}
                                            ></button>
                                            <div class="button-animation"></div>
                                            <div class="button-content">
                                                <span class="content-text">{this.getButtonText(parameters.selectedLocation)}</span>
                                            </div>
                                        </div>
                                    </div>
                                }

                                {parameters.selectedRegion && parameters.selectedLocation &&
                                    <div class="module-checkbox custom-control custom-checkbox">
                                        <input type="checkbox"
                                            id="keep-my-selection"
                                            name="keep_my_selection"
                                            class="custom-control-input"
                                            // disabled={ (parameters.selectedRegion && parameters.selectedLocation) ? true : false }
                                            onChange={($event) => this.changeCheckboxValueChecked(($event.target as HTMLInputElement).checked)}
                                            checked={this.pageInfo.sessionCreated}
                                        />
                                        <label class="custom-control-label"
                                            htmlFor="keep-my-selection">
                                            Keep my selection during the current session
                                        </label>
                                    </div>
                                }

                            </div>
                        }

                        {this.pageInfo.gtlSessionRedirectURL &&
                            <div>
                                <div class="modal-header">
                                    <p class="headline h2 modal-title">
                                        You will be automatically redirected ...
                                    </p>
                                </div>
                                <div class="modal-body">
                                    <p>
                                        {this.pageInfo.gtlSessionRedirectURL}
                                    </p>
                                </div>
                            </div>
                        }

                        {noInfo && !this.pageInfo.gtlSessionRedirectURL &&
                            <div>
                                <div class="modal-header">
                                    <p class="headline h2 modal-title">
                                        Page not found
                                    </p>
                                </div>
                                <div class="modal-body">
                                    <p class="lead">
                                        We're sorry, your request encountered an error.
                                    </p>
                                    <p></p>
                                    <p>
                                        <strong>Help us to improve our Website</strong>
                                    </p>
                                    <p></p>
                                    <p>
                                        If a link does not work, please let us know. Please send an e-mail to the
                                        Webmaster.
                                    </p>
                                    <p>
                                        <a href="mailto:webmaster.portal@boschrexroth.de?subject=Link to local website not working">
                                            <span class="icon icon-dc_mail"></span> Contact Webmaster
                                        </a>
                                    </p>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>

        );
    }

    protected checkForClose(event: UIEvent) {
        event.stopPropagation();

        if (!event.target) {
            return;
        }

        if ((event.target as HTMLElement).classList.contains('module-modal') && this.closable) {
            this.hideDialog();
        }
    }

    public setModalStatus(active: boolean) {
        let documentClasses = document.documentElement.classList;
        documentClasses.toggle("modal-active", active);
    }

    /**
     * Returns all variables to define the displayed elements
     * @protected
     */
    protected getDisplayParameters(): IDisplayParameters {
        const result: IDisplayParameters = {
            noInfo: Object.keys(this.pageInfo?.references ?? {}).length <= 0,
            parameters: {
                selectedRegion: this.suggestedRegion,
                selectedLocation: this.suggestedLocation,
                showRedirectInfo: false,
                showInvalidContentInfo: this.validGlobalPageId === GlobalIdValidation.Invalid
            },
        };

        // No info, so nothing else to set
        if (result.noInfo) {
            return result;
        }

        // For when there is no page for the location
        // and the user has to be redirected to another location
        if (this.suggestedLocation) {
            const targetWebsite = this.pageInfo.references[this.pageId][this.suggestedRegion]?.[this.suggestedLocation]?.website;

            // if targetWebsite is not available, reset selected values to null
            if (targetWebsite === undefined) {
                result.parameters.selectedRegion = null;
                result.parameters.selectedLocation = null;
                return result;
            }

            if (targetWebsite != this.suggestedLocation) {
                result.parameters.selectedLocation = targetWebsite;
                result.parameters.showRedirectInfo = true;
                return result;
            }
        }

        return result;
    }

    @Listen('locationChange')
    onLocationChange({detail}: CustomEvent<string>) {
        this.setLocation(detail);
        if (this.linkType === 'EXTERNAL' || this.linkType === 'WEB_CONTENT') {
            this.fetchNewLocationInfo();
        }
    }

    @Listen('regionChange')
    onRegionChange({detail}: CustomEvent<string>) {
        this.setRegion(detail);
    }



    protected openLocation(region = this.suggestedRegion, location = this.suggestedLocation) {
        const url = this.pageInfo.references[this.pageId][region][location].url;
        const mergedUrl = mergeUrlWithQueryParameters(url, window.location.href);
        const action = this.dialogTitle + '<>' + this.buttonLablePrefix + location;

        window.utag?.link({
            event_type: 'click',
            event_subtype: 'countryswitch',
            event_action: action.replace(/\W/g, '_').toLowerCase(),
            event_label: url,
            event_reference: 'modal',
        });

        this.emitLanguageChangeEvent(new URL(mergedUrl));

        // give event tracking a bit of air
        setTimeout(() => window.location.assign(mergedUrl));
    }

    protected redirectToTargetURL(redirectURL: string) {
        const mergedUrl = mergeUrlWithQueryParameters(redirectURL, window.location.href);

        this.emitLanguageChangeEvent(new URL(mergedUrl), true);
        setTimeout(() => window.location.assign(mergedUrl), 2000);
    }

    protected setLocation(newLocation?: string) {
        this.suggestedLocation = newLocation;
    }

    protected setRegion(newRegion?: string) {
        this.setLocation(undefined);
        this.suggestedRegion = newRegion;
    }

    protected getLanguageLabel(region = this.suggestedRegion, location = this.suggestedLocation) : string  {
        if (this.pageId && this.pageInfo?.references && region && location) {
            const lang = this.pageInfo.references[this.pageId][region][location].locale.substring(3);
            if (lang) {
                const languageLabel = new Intl.DisplayNames(['en'], {type: 'language'}).of(lang);
                return `in ${languageLabel}`;
            }
        }
        return '';
    }

    protected getLanguageCode(region = this.suggestedRegion, location = this.suggestedLocation) : string  {
        if (this.pageId && this.pageInfo?.references && region && location) {
            const lang = this.pageInfo.references[this.pageId][region][location].locale.substring(3);

            if (lang) {
                return lang;
            }
        }
        return '';
    }

    protected getCountryCode(region = this.suggestedRegion, location = this.suggestedLocation) : string  {
        if (this.pageId && this.pageInfo?.references && region && location) {
            const country = this.pageInfo.references[this.pageId][region][location].locale.substring(0, 2);

            if (country) {
                return country;
            }
        }
        return '';
    }


    private getButtonText(selectedLocation: string): string {
        if(this.validGlobalPageId === GlobalIdValidation.Valid) {
            return this.buttonLablePrefix + selectedLocation;
        } else {
            return this.buttonLabelNoContentPrefix + selectedLocation;
        }
    }

}
